import { useRef, useState } from 'react';
import { FormApi, FormValues } from 'informed';
import api from 'api';
import { getResizedImage } from 'shared-helpers/common';
import { useDispatch, useReduxState } from 'hooks';
import { defaultTheme } from 'styles';
import { useApiData } from 'lib/api';
import { waitForImage } from 'lib/files';
import { HomePageSettingsBanner } from 'components/admin/settings/homepage';
import { UserAttributesConditionsManager } from 'components/admin/settings/userAttributes';
import { Alert, Badge, Button, ContentBox } from 'components/common';
import { Box, BoxBackLink } from 'components/common/box';
import {
  Checkbox,
  ColorPicker,
  Form,
  FormGroup,
  FormSection,
  ImageUploader,
  LanguageVariants,
  NumberStepper,
  Text
} from 'components/common/form';
import { TextInput } from 'components/common/form/common';
import {
  SiteSettingsThemesEditHighlight,
  SiteSettingsThemesExport,
  SiteSettingsThemesImport
} from '.';

import type { GetUserCustomAttributesRes } from 'shared-types/responses';

const SiteSettingsThemesEdit = () => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { themeId },
    serverConfig
  } = useReduxState();

  const bannerImageUploader = useRef<any>(undefined);
  const formApi = useRef<FormApi<FormValues>>(undefined);
  const emailLogoUploader = useRef<any>(undefined);
  const emailLogoVariantsUploader = useRef<any>({});
  const loginBackgroundImageUploader = useRef<any>(undefined);
  const logoUploader = useRef<any>(undefined);
  const logoVariantsUploader = useRef<any>({});
  const mobileLogoUploader = useRef<any>(undefined);

  const [saving, setSaving] = useState(false);

  const { data, loading, updateData } = useApiData({
    onlyIf: Boolean(themeId),
    url: `/admin/server-themes/${themeId}`
  });

  const theme = data?.theme;

  const { data: attributeData } = useApiData<GetUserCustomAttributesRes>({
    url: '/admin/user-custom-attributes'
  });

  const userCustomAttributes = attributeData?.userCustomAttributes || [];

  const availableAttributes = {};
  userCustomAttributes.forEach((availableAttribute) => {
    availableAttributes[availableAttribute.key] = availableAttribute;
  });

  const handleImport = async () => {
    await updateData({ showLoading: true });
    if (themeId === serverConfig.customThemeId) {
      dispatch.updateTheme(themeId, theme.data);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setSaving(true);

      if (logoUploader.current?.state.imageAdded) {
        const file = await logoUploader.current.upload();
        if (file) values.data.logo = file.url;
      }

      if (logoVariantsUploader.current) {
        values.data.logoLanguageVariants =
          values.data.logoLanguageVariants || {};

        await Promise.all(
          Object.keys(logoVariantsUploader.current).map(async (key) => {
            if (logoVariantsUploader.current[key]?.state.imageAdded) {
              const file = await logoVariantsUploader.current[key].upload();
              if (file) values.data.logoLanguageVariants[key] = file.url;
            }
          })
        );
      }

      if (mobileLogoUploader.current?.state.imageAdded) {
        const file = await mobileLogoUploader.current.upload();
        if (file) values.data.mobileLogo = file.url;
      }

      if (emailLogoUploader.current?.state.imageAdded) {
        const file = await emailLogoUploader.current.upload();
        if (file) values.data.emailLogo = file.url;
      }

      if (emailLogoVariantsUploader.current) {
        values.data.emailLogoLanguageVariants =
          values.data.emailLogoLanguageVariants || {};

        await Promise.all(
          Object.keys(emailLogoVariantsUploader.current).map(async (key) => {
            if (emailLogoVariantsUploader.current[key]?.state.imageAdded) {
              const file = await emailLogoVariantsUploader.current[
                key
              ].upload();
              if (file) values.data.emailLogoLanguageVariants[key] = file.url;
            }
          })
        );
      }

      if (loginBackgroundImageUploader.current?.state.imageAdded) {
        const file = await loginBackgroundImageUploader.current.upload();
        if (file) values.data.loginBackgroundImage = file.url;
      }

      if (bannerImageUploader.current?.state.file) {
        const file = await bannerImageUploader.current.upload();
        if (file) {
          values.data.homePageBannerImage = file.url;
          // wait for resized image to be available
          await waitForImage(getResizedImage({ size: 1920, url: file.url }));
        }
      }

      await api.put(`/admin/server-themes/${themeId}`, values);

      setSaving(false);
      if (themeId === serverConfig.customThemeId) {
        dispatch.updateTheme(themeId, values.data);
      }
      dispatch.addNotice({
        text: messages.siteThemesUpdateSuccess,
        type: 'success'
      });
    } catch (err) {
      setSaving(false);
      await dispatch.handleErrorDispatch(err);
    }
  };

  return (
    <div className="settings">
      <Box
        headerContent={
          <SiteSettingsThemesImport onImport={handleImport} themeId={themeId} />
        }
        loading={loading}
        padding={0}
        title={
          <BoxBackLink
            as="/admin/settings/themes"
            href="/admin/settings/themes"
            text={messages.siteThemesAll}
          />
        }
      >
        {!loading && theme ? (
          <Form
            getApi={(informedApi) => (formApi.current = informedApi)}
            initialValues={{
              title: theme.title,
              data: {
                ...theme.data,
                certificateLogoWidth: theme.data.certificateLogoWidth || 150,
                homePageBannerImage: theme.data.homePageBannerImage,
                homePageBannerImageLayout:
                  theme.data.homePageBannerImageLayout || 'full-width',
                homePageBannerImagePosition:
                  theme.data.homePageBannerImagePosition || 'center center',
                mobileLogoWidth: theme.data.mobileLogoWidth || 132
              }
            }}
            onSubmit={handleSubmit}
          >
            {({ formState }) => (
              <div>
                <FormSection>
                  <FormGroup label={messages.commonTitle}>
                    <Text
                      field="title"
                      style={{ maxWidth: '100%', width: 300 }}
                    />
                  </FormGroup>
                  <FormGroup label="Attribute based theme">
                    <Checkbox
                      field="data.enableWithAttributeConditions.enabled"
                      id="enable-with-attribute-conditions"
                      label="Enable as a preferred theme with attribute conditions"
                      labelTooltip="If this is the default theme, the conditions will be ignored"
                    />
                    {formState.values.data?.enableWithAttributeConditions
                      ?.enabled && (
                      <UserAttributesConditionsManager
                        availableAttributes={availableAttributes}
                        fieldPrefix="data.enableWithAttributeConditions"
                        includeEmailCondition={true}
                        includeEmailDomainCondition={true}
                        style={{ marginTop: 5 }}
                      />
                    )}
                  </FormGroup>
                </FormSection>

                <FormSection title={messages.commonLogo}>
                  <FormGroup label={messages.commonLogo}>
                    <ImageUploader
                      field="data.logo"
                      id="custom-theme-logo"
                      maxWidth={formState.values.data?.logoWidth}
                      messages={messages}
                      setUploader={(imageUploader) =>
                        (logoUploader.current = imageUploader)
                      }
                      showPreviewImage={true}
                    />

                    <LanguageVariants
                      customProps={{
                        localeWidth:
                          formState.values.data?.logoWidthLanguageVariants ||
                          {},
                        localeUploaders: logoVariantsUploader.current,
                        maxWidth: formState.values.data?.logoWidth,
                        messages,
                        showPreviewImage: true
                      }}
                      field="data.logoLanguageVariants"
                      type="image"
                    />
                  </FormGroup>

                  <FormGroup>
                    <NumberStepper
                      field="data.logoWidth"
                      id="custom-theme-logo-width"
                      label="logoWidth"
                      min={0}
                      stepValue={1}
                      style={{ marginTop: 10 }}
                    />

                    <LanguageVariants
                      customProps={{ min: 0, stepValue: 1 }}
                      field="data.logoWidthLanguageVariants"
                      type="number"
                    />
                  </FormGroup>

                  <FormGroup>
                    <NumberStepper
                      field="data.certificateLogoWidth"
                      id="custom-theme-certificate-logo-width"
                      label="certificateLogoWidth"
                      min={0}
                      stepValue={1}
                      style={{ marginTop: 10 }}
                    />
                  </FormGroup>

                  <FormGroup label="mobileLogo">
                    <ImageUploader
                      field="data.mobileLogo"
                      id="custom-theme-mobile-logo"
                      maxWidth={formState.values.data?.mobileLogoWidth}
                      messages={messages}
                      setUploader={(imageUploader) =>
                        (mobileLogoUploader.current = imageUploader)
                      }
                      showPreviewImage={true}
                    />
                  </FormGroup>

                  <FormGroup>
                    <NumberStepper
                      field="data.mobileLogoWidth"
                      id="custom-theme-mobile-logo-width"
                      label="mobileLogoWidth"
                      min={0}
                      stepValue={1}
                      style={{ marginTop: 10 }}
                    />
                  </FormGroup>

                  <FormGroup label="emailLogo">
                    <ImageUploader
                      field="data.emailLogo"
                      id="custom-theme-email-logo"
                      maxWidth={formState.values.data?.emailLogoWidth}
                      messages={messages}
                      setUploader={(imageUploader) =>
                        (emailLogoUploader.current = imageUploader)
                      }
                      showPreviewImage={true}
                    />

                    <LanguageVariants
                      customProps={{
                        localeWidth:
                          formState.values.data
                            ?.emailLogoWidthLanguageVariants || {},
                        localeUploaders: emailLogoVariantsUploader.current,
                        maxWidth: formState.values.data?.logoWidth,
                        messages,
                        showPreviewImage: true
                      }}
                      field="data.emailLogoLanguageVariants"
                      type="image"
                    />
                  </FormGroup>

                  <FormGroup label="emailLogoAlign">
                    <Text
                      field="data.emailLogoAlign"
                      style={{ maxWidth: '100%', width: 160 }}
                    />
                  </FormGroup>

                  <FormGroup>
                    <NumberStepper
                      field="data.emailLogoWidth"
                      id="custom-theme-email-logo-width"
                      label="emailLogoWidth"
                      min={0}
                      stepValue={1}
                      style={{ marginTop: 10 }}
                    />

                    <LanguageVariants
                      customProps={{ min: 0, stepValue: 1 }}
                      field="data.emailLogoWidthLanguageVariants"
                      type="number"
                    />
                  </FormGroup>
                </FormSection>

                <FormSection maxWidth="100%" title="Common">
                  <div className="row-md">
                    <div className="col-1of4">
                      <FormGroup label="linkColor">
                        <ColorPicker field="data.linkColor" />
                      </FormGroup>
                    </div>
                    <div className="col-1of4">
                      <FormGroup label="certificateHighlightColor">
                        <ColorPicker field="data.certificateHighlightColor" />
                      </FormGroup>
                    </div>
                    <div className="col-1of4">
                      <FormGroup label="chartMainColor">
                        <ColorPicker field="data.chartMainColor" />
                      </FormGroup>
                    </div>
                  </div>
                </FormSection>

                <FormSection maxWidth="100%" title="Login">
                  <div className="row-md">
                    <div className="col-1of4">
                      <FormGroup label="loginBackgroundColor">
                        <ColorPicker field="data.loginBackgroundColor" />
                      </FormGroup>
                    </div>
                  </div>

                  <div className="row-md">
                    <div className="col">
                      <FormGroup label="loginBackgroundImage">
                        <ImageUploader
                          field="data.loginBackgroundImage"
                          id="custom-theme-login-background-image"
                          maxWidth={200}
                          messages={messages}
                          setUploader={(imageUploader) =>
                            (loginBackgroundImageUploader.current =
                              imageUploader)
                          }
                          showPreviewImage={true}
                        />
                      </FormGroup>
                    </div>
                  </div>

                  <div className="row-md">
                    <div className="col-1of4">
                      <FormGroup label="loginBackgroundImagePosition">
                        <Text
                          field="data.loginBackgroundImagePosition"
                          placeholder="0% 0%"
                        />
                      </FormGroup>
                    </div>
                  </div>
                </FormSection>

                <FormSection maxWidth="100%" title={messages.homePage}>
                  <FormGroup
                    maxWidth="100%"
                    label={messages.homePageSettingsBanner}
                  >
                    <HomePageSettingsBanner
                      fieldPrefix="data."
                      setUploader={(uploader) =>
                        (bannerImageUploader.current = uploader)
                      }
                    />
                  </FormGroup>
                </FormSection>

                <FormSection maxWidth="100%" title="Header">
                  <div className="row-md" style={{ marginTop: 15 }}>
                    <div className="col-1of4">
                      <FormGroup label="headerBackgroundColor">
                        <ColorPicker field="data.headerBackgroundColor" />
                      </FormGroup>
                    </div>
                    <div className="col-1of4">
                      <FormGroup label="headerButtonBackgroundColor">
                        <ColorPicker field="data.headerButtonBackgroundColor" />
                      </FormGroup>
                    </div>
                    <div className="col-1of4">
                      <FormGroup label="headerButtonBackgroundColorHover">
                        <ColorPicker field="data.headerButtonBackgroundColorHover" />
                      </FormGroup>
                    </div>
                  </div>

                  <div className="row-md" style={{ marginTop: 15 }}>
                    <div className="col-1of4">
                      <FormGroup label="headerIconColor">
                        <ColorPicker field="data.headerIconColor" />
                      </FormGroup>
                    </div>
                    <div className="col-1of4">
                      <FormGroup label="headerTextColor">
                        <ColorPicker field="data.headerTextColor" />
                      </FormGroup>
                    </div>
                  </div>

                  <div className="row-md" style={{ marginTop: 15 }}>
                    <div className="col-1of4">
                      <FormGroup label="headerPadding">
                        <Text
                          field="data.headerPadding"
                          style={{ maxWidth: '100%' }}
                        />
                      </FormGroup>
                    </div>
                  </div>
                </FormSection>

                <FormSection maxWidth="100%" title="SubHeader">
                  <div className="row-md" style={{ marginTop: 15 }}>
                    <div className="col-1of4">
                      <FormGroup label="subHeaderBackgroundColor">
                        <ColorPicker field="data.subHeaderBackgroundColor" />
                      </FormGroup>
                    </div>
                    <div className="col-1of4">
                      <FormGroup label="subHeaderColorActive">
                        <ColorPicker field="data.subHeaderColorActive" />
                      </FormGroup>
                    </div>
                    <div className="col-1of4">
                      <FormGroup label="subHeaderColor">
                        <ColorPicker field="data.subHeaderColor" />
                      </FormGroup>
                    </div>
                  </div>
                </FormSection>

                <FormSection maxWidth="100%" title="Footer">
                  <div className="row-md" style={{ marginTop: 15 }}>
                    <div className="col-1of4">
                      <FormGroup label="footerBackgroundColor">
                        <ColorPicker field="data.footerBackgroundColor" />
                      </FormGroup>
                    </div>
                    <div className="col-1of4">
                      <FormGroup label="footerColor">
                        <ColorPicker field="data.footerTextColor" />
                      </FormGroup>
                    </div>
                  </div>
                </FormSection>

                <FormSection maxWidth="100%" title="Page">
                  <div className="row-md" style={{ marginTop: 15 }}>
                    <div className="col-1of4">
                      <FormGroup label="pageBackgroundColor">
                        <ColorPicker field="data.pageBackgroundColor" />
                      </FormGroup>
                    </div>
                  </div>
                  <div className="row-md" style={{ marginTop: 15 }}>
                    <div className="col-1of4">
                      <FormGroup label="pageTextColor">
                        <ColorPicker field="data.pageTextColor" />
                      </FormGroup>
                    </div>
                    <div className="col-1of4">
                      <FormGroup label="pageTextColorLighter">
                        <ColorPicker field="data.pageTextColorLighter" />
                      </FormGroup>
                    </div>
                  </div>
                  <div className="row-md" style={{ marginTop: 15 }}>
                    <div className="col-1of4">
                      <FormGroup label="pageIconBackgroundColor">
                        <ColorPicker field="data.pageIconBackgroundColor" />
                      </FormGroup>
                    </div>
                    <div className="col-1of4">
                      <FormGroup label="pageIconTextColor">
                        <ColorPicker field="data.pageIconTextColor" />
                      </FormGroup>
                    </div>
                    <div className="col-1of4">
                      <FormGroup label="pageIconTextColorLighter">
                        <ColorPicker field="data.pageIconTextColorLighter" />
                      </FormGroup>
                    </div>
                  </div>
                </FormSection>

                <FormSection maxWidth="100%" title="Main">
                  <div className="row-md">
                    <div className="col-1of4">
                      <FormGroup label="mainColor">
                        <ColorPicker field="data.mainColor" />
                      </FormGroup>
                    </div>
                    <div className="col-1of4">
                      <FormGroup label="mainColorActive">
                        <ColorPicker field="data.mainColorActive" />
                      </FormGroup>
                    </div>
                    <div className="col-1of4">
                      <FormGroup label="mainTextColor">
                        <ColorPicker field="data.mainTextColor" />
                      </FormGroup>
                    </div>
                  </div>

                  <div className="preview">
                    <div className="preview-item">
                      <Button
                        customBackgroundColor={formState.values.data?.mainColor}
                        customBackgroundColorHover={
                          formState.values.data?.mainColorActive
                        }
                        customColor={formState.values.data?.mainTextColor}
                        text="Button"
                      />
                    </div>
                  </div>
                </FormSection>

                <FormSection maxWidth="100%" title="Success">
                  <div className="row-md">
                    <div className="col-1of4">
                      <FormGroup label="successColor">
                        <ColorPicker field="data.successColor" />
                      </FormGroup>
                    </div>
                    <div className="col-1of4">
                      <FormGroup label="successColorActive">
                        <ColorPicker field="data.successColorActive" />
                      </FormGroup>
                    </div>
                    <div className="col-1of4">
                      <FormGroup label="successTextColor">
                        <ColorPicker field="data.successTextColor" />
                      </FormGroup>
                    </div>
                  </div>

                  <div className="row-md">
                    <div className="col-1of4">
                      <FormGroup label="successColorDark">
                        <ColorPicker field="data.successColorDark" />
                      </FormGroup>
                    </div>
                    <div className="col-1of4">
                      <FormGroup label="successColorDarkActive">
                        <ColorPicker field="data.successColorDarkActive" />
                      </FormGroup>
                    </div>
                    <div className="col-1of4">
                      <FormGroup label="successTextColorDark">
                        <ColorPicker field="data.successTextColorDark" />
                      </FormGroup>
                    </div>
                  </div>

                  <div className="row-md">
                    <div className="col-1of4">
                      <FormGroup label="successLightColor">
                        <ColorPicker field="data.successLightColor" />
                      </FormGroup>
                    </div>
                    <div className="col-1of4">
                      <FormGroup label="successLightTextColor">
                        <ColorPicker field="data.successLightTextColor" />
                      </FormGroup>
                    </div>
                  </div>

                  <div className="row-md">
                    <div className="col-1of4">
                      <FormGroup label="successStandaloneColor">
                        <ColorPicker field="data.successStandaloneColor" />
                      </FormGroup>
                    </div>
                  </div>

                  <div className="preview">
                    <div className="preview-item">
                      <Button
                        category="success"
                        customBackgroundColor={
                          formState.values.data?.successColor
                        }
                        customBackgroundColorHover={
                          formState.values.data?.successColorActive
                        }
                        customColor={formState.values.data?.successTextColor}
                        text="Button"
                      />
                    </div>
                    <div className="preview-item">
                      <div className="preview-item-inline">
                        <Badge
                          category="success"
                          text="Badge"
                          style={{
                            color: formState.values.data?.successTextColor,
                            backgroundColor: formState.values.data?.successColor
                          }}
                        />
                      </div>
                      <div className="preview-item-inline">
                        <Badge
                          category="success-light"
                          text="Badge"
                          style={{
                            color: formState.values.data?.successLightTextColor,
                            backgroundColor:
                              formState.values.data?.successLightColor
                          }}
                        />
                      </div>
                    </div>
                    <div className="preview-item">
                      <div className="preview-item-inline">
                        <Alert
                          type="success-light"
                          text="Lorem ipsum dolor sit amet"
                          style={{
                            color: formState.values.data?.successLightTextColor,
                            backgroundColor:
                              formState.values.data?.successLightColor
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </FormSection>

                <FormSection maxWidth="100%" title="Danger">
                  <div className="row-md">
                    <div className="col-1of4">
                      <FormGroup label="dangerColor">
                        <ColorPicker field="data.dangerColor" />
                      </FormGroup>
                    </div>
                    <div className="col-1of4">
                      <FormGroup label="dangerColorActive">
                        <ColorPicker field="data.dangerColorActive" />
                      </FormGroup>
                    </div>
                    <div className="col-1of4">
                      <FormGroup label="dangerTextColor">
                        <ColorPicker field="data.dangerTextColor" />
                      </FormGroup>
                    </div>
                  </div>

                  <div className="row-md">
                    <div className="col-1of4">
                      <FormGroup label="dangerLightColor">
                        <ColorPicker field="data.dangerLightColor" />
                      </FormGroup>
                    </div>
                    <div className="col-1of4">
                      <FormGroup label="dangerLightTextColor">
                        <ColorPicker field="data.dangerLightTextColor" />
                      </FormGroup>
                    </div>
                  </div>

                  <div className="preview">
                    <div className="preview-item">
                      <Button
                        category="danger"
                        customBackgroundColor={
                          formState.values.data?.dangerColor
                        }
                        customBackgroundColorHover={
                          formState.values.data?.dangerColorActive
                        }
                        customColor={formState.values.data?.dangerTextColor}
                        text="Button"
                      />
                    </div>
                    <div className="preview-item">
                      <div className="preview-item-inline">
                        <Badge
                          category="error"
                          text="Badge"
                          style={{
                            color: formState.values.data?.dangerTextColor,
                            backgroundColor: formState.values.data?.dangerColor
                          }}
                        />
                      </div>
                      <div className="preview-item-inline">
                        <Badge
                          category="error-light"
                          text="Badge"
                          style={{
                            color: formState.values.data?.dangerLightTextColor,
                            backgroundColor:
                              formState.values.data?.dangerLightColor
                          }}
                        />
                      </div>
                    </div>
                    <div className="preview-item">
                      <div className="preview-item-inline">
                        <Alert
                          type="error-light"
                          text="Lorem ipsum dolor sit amet"
                          style={{
                            color: formState.values.data?.dangerLightTextColor,
                            backgroundColor:
                              formState.values.data?.dangerLightColor
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </FormSection>

                <FormSection maxWidth="100%" title="Warning">
                  <div className="row-md">
                    <div className="col-1of4">
                      <FormGroup label="warningColor">
                        <ColorPicker field="data.warningColor" />
                      </FormGroup>
                    </div>
                    <div className="col-1of4">
                      <FormGroup label="warningTextColor">
                        <ColorPicker field="data.warningTextColor" />
                      </FormGroup>
                    </div>
                  </div>

                  <div className="row-md">
                    <div className="col-1of4">
                      <FormGroup label="warningLightColor">
                        <ColorPicker field="data.warningLightColor" />
                      </FormGroup>
                    </div>
                    <div className="col-1of4">
                      <FormGroup label="warningLightTextColor">
                        <ColorPicker field="data.warningLightTextColor" />
                      </FormGroup>
                    </div>
                  </div>

                  <div className="row-md">
                    <div className="col-1of4">
                      <FormGroup label="warningStandaloneColor">
                        <ColorPicker field="data.warningStandaloneColor" />
                      </FormGroup>
                    </div>
                  </div>

                  <div className="preview">
                    <div className="preview-item">
                      <div className="preview-item-inline">
                        <Badge
                          category="warning"
                          text="Badge"
                          style={{
                            color: formState.values.data?.warningTextColor,
                            backgroundColor: formState.values.data?.warningColor
                          }}
                        />
                      </div>
                      <div className="preview-item-inline">
                        <Badge
                          category="warning-light"
                          text="Badge"
                          style={{
                            color: formState.values.data?.warningLightTextColor,
                            backgroundColor:
                              formState.values.data?.warningLightColor
                          }}
                        />
                      </div>
                    </div>
                    <div className="preview-item">
                      <div className="preview-item-inline">
                        <Alert
                          type="warning-light"
                          text="Lorem ipsum dolor sit amet"
                          style={{
                            color: formState.values.data?.warningLightTextColor,
                            backgroundColor:
                              formState.values.data?.warningLightColor
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </FormSection>

                <FormSection maxWidth="100%" title="Grey">
                  <div className="row-md">
                    <div className="col-1of4">
                      <FormGroup label="lightGreyLighter">
                        <ColorPicker field="data.lightGreyLighter" />
                      </FormGroup>
                    </div>
                  </div>
                  <div className="row-md">
                    <div className="col-1of4">
                      <FormGroup label="lightGrey">
                        <ColorPicker field="data.lightGrey" />
                      </FormGroup>
                    </div>
                    <div className="col-1of4">
                      <FormGroup label="lightGreyActive">
                        <ColorPicker field="data.lightGreyActive" />
                      </FormGroup>
                    </div>
                  </div>
                  <div className="row-md">
                    <div className="col-1of4">
                      <FormGroup label="lightGreyDark">
                        <ColorPicker field="data.lightGreyDark" />
                      </FormGroup>
                    </div>
                    <div className="col-1of4">
                      <FormGroup label="lightGreyDarkActive">
                        <ColorPicker field="data.lightGreyDarkActive" />
                      </FormGroup>
                    </div>
                  </div>

                  <div className="preview">
                    <div className="preview-item">
                      <div className="preview-item-inline">
                        <Button
                          category="lighter"
                          customBackgroundColor={
                            formState.values.data?.lightGrey
                          }
                          customBackgroundColorHover={
                            formState.values.data?.lightGreyActive
                          }
                          text="Light grey"
                        />
                      </div>
                      <div className="preview-item-inline">
                        <Button
                          category="light"
                          customBackgroundColor={
                            formState.values.data?.lightGreyDark
                          }
                          customBackgroundColorHover={
                            formState.values.data?.lightGreyDarkActive
                          }
                          text="Light grey dark"
                        />
                      </div>
                    </div>
                    <div className="preview-item">
                      <div className="preview-item-inline">
                        <Alert
                          type="info"
                          text="Lorem ipsum dolor sit amet"
                          style={{
                            backgroundColor: formState.values.data?.lightGrey
                          }}
                        />
                      </div>
                    </div>
                    <div className="preview-item">
                      <ContentBox
                        style={{
                          backgroundColor: formState.values.data?.lightGrey,
                          display: 'inline-block'
                        }}
                      >
                        <div className="preview-item-inline">
                          <Button
                            category="light"
                            customBackgroundColor={
                              formState.values.data?.lightGreyDark
                            }
                            customBackgroundColorHover={
                              formState.values.data?.lightGreyDarkActive
                            }
                            text="Light grey dark"
                          />
                        </div>
                        <div className="preview-item-inline">
                          <Alert
                            type="info-dark"
                            text="Lorem ipsum dolor sit amet"
                            style={{
                              backgroundColor:
                                formState.values.data?.lightGreyDark
                            }}
                          />
                        </div>
                      </ContentBox>
                    </div>
                  </div>
                </FormSection>

                <FormSection maxWidth="100%" title="Text">
                  <div className="row-md" style={{ marginTop: 15 }}>
                    <div className="col-1of4">
                      <FormGroup label="textColor">
                        <ColorPicker field="data.textColor" />
                      </FormGroup>
                    </div>
                    <div className="col-1of4">
                      <FormGroup label="textColorLight">
                        <ColorPicker field="data.textColorLight" />
                      </FormGroup>
                    </div>
                    <div className="col-1of4">
                      <FormGroup label="textColorLighter">
                        <ColorPicker field="data.textColorLighter" />
                      </FormGroup>
                    </div>
                  </div>

                  <div className="preview" style={{ fontSize: '1.6rem' }}>
                    <div
                      className="preview-item"
                      style={{ color: formState.values.data?.textColor }}
                    >
                      <div
                        className="preview-item-inline"
                        style={{ fontWeight: 700 }}
                      >
                        {'Text color'}
                      </div>
                      <div
                        className="preview-item-inline"
                        style={{ fontWeight: 600 }}
                      >
                        {'Text color'}
                      </div>
                      <div
                        className="preview-item-inline"
                        style={{ fontWeight: 400 }}
                      >
                        {'Text color'}
                      </div>
                    </div>

                    <div
                      className="preview-item"
                      style={{ color: formState.values.data?.textColorLight }}
                    >
                      <div
                        className="preview-item-inline"
                        style={{ fontWeight: 700 }}
                      >
                        {'Text color light'}
                      </div>
                      <div
                        className="preview-item-inline"
                        style={{ fontWeight: 600 }}
                      >
                        {'Text color light'}
                      </div>
                      <div
                        className="preview-item-inline"
                        style={{ fontWeight: 400 }}
                      >
                        {'Text color light'}
                      </div>
                    </div>

                    <div
                      className="preview-item"
                      style={{ color: formState.values.data?.textColorLighter }}
                    >
                      <div
                        className="preview-item-inline"
                        style={{ fontWeight: 700 }}
                      >
                        {'Text color lighter'}
                      </div>
                      <div
                        className="preview-item-inline"
                        style={{ fontWeight: 600 }}
                      >
                        {'Text color lighter'}
                      </div>
                      <div
                        className="preview-item-inline"
                        style={{ fontWeight: 400 }}
                      >
                        {'Text color lighter'}
                      </div>
                    </div>
                  </div>
                </FormSection>

                <SiteSettingsThemesEditHighlight
                  field="data.textHighlightColors"
                  formState={formState}
                />

                <FormSection maxWidth="100%" title="Card">
                  <FormGroup label="cardBoxShadow">
                    <Text
                      field="data.cardBoxShadow"
                      style={{ maxWidth: '100%' }}
                    />
                  </FormGroup>
                  <FormGroup label="cardBoxShadowActive">
                    <Text
                      field="data.cardBoxShadowActive"
                      style={{ maxWidth: '100%' }}
                    />
                  </FormGroup>

                  <div
                    className="preview"
                    style={{
                      backgroundColor:
                        formState.values.data?.pageBackgroundColor,
                      display: 'flex',
                      gap: 20,
                      padding: 20,
                      width: 500
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: 'white',
                        borderRadius: formState.values.data?.cardBorderRadius,
                        boxShadow: formState.values.data?.cardBoxShadow,
                        padding: 15
                      }}
                    >
                      <div>Some content</div>
                      <div>Some content</div>
                      <div>Some content</div>
                    </div>

                    <div
                      style={{
                        backgroundColor: 'white',
                        borderRadius: formState.values.data?.cardBorderRadius,
                        boxShadow: formState.values.data?.cardBoxShadowActive,
                        padding: 15
                      }}
                    >
                      <div>Some content</div>
                      <div>Some content</div>
                      <div>Some content</div>
                    </div>
                  </div>
                </FormSection>

                <FormSection maxWidth="100%" title="Radius">
                  <div className="row-lg">
                    <div className="col-1of4">
                      <FormGroup label="buttonBorderRadius">
                        <Text
                          field="data.buttonBorderRadius"
                          style={{ width: '70px' }}
                        />
                      </FormGroup>
                    </div>

                    <div className="col-1of4">
                      <FormGroup label="cardBorderRadius">
                        <Text
                          field="data.cardBorderRadius"
                          style={{ width: '70px' }}
                        />
                      </FormGroup>
                    </div>

                    <div className="col-1of4">
                      <FormGroup label="dropdownBorderRadius">
                        <Text
                          field="data.dropdownBorderRadius"
                          style={{ width: '70px' }}
                        />
                      </FormGroup>
                    </div>

                    <div className="col-1of4">
                      <FormGroup label="inputBorderRadius">
                        <Text
                          field="data.inputBorderRadius"
                          style={{ width: '70px' }}
                        />
                      </FormGroup>
                    </div>
                  </div>

                  <div className="preview">
                    <div className="row-lg">
                      <div className="col-1of4">
                        <Button
                          category="default"
                          style={{
                            borderRadius:
                              formState.values.data?.buttonBorderRadius
                          }}
                          text="Test button"
                        />
                      </div>

                      <div className="col-1of4">
                        <div
                          style={{
                            backgroundColor:
                              formState.values.data?.pageBackgroundColor,
                            padding: 20,
                            width: 180
                          }}
                        >
                          <div
                            style={{
                              backgroundColor: 'white',
                              borderRadius:
                                formState.values.data?.cardBorderRadius,
                              boxShadow: formState.values.data?.cardBoxShadow,
                              padding: 15
                            }}
                          >
                            <div>Some content</div>
                            <div>Some content</div>
                            <div>Some content</div>
                          </div>
                        </div>
                      </div>

                      <div className="col-1of4">
                        <div
                          style={{
                            backgroundColor: 'white',
                            borderRadius:
                              formState.values.data?.dropdownBorderRadius,
                            boxShadow: defaultTheme.dropdownShadow,
                            padding: '10px 15px',
                            width: 200
                          }}
                        >
                          Some content
                        </div>
                      </div>

                      <div className="col-1of4">
                        <TextInput
                          placeholder={'Placeholder'}
                          style={{
                            borderRadius:
                              formState.values.data?.inputBorderRadius,
                            width: 200
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </FormSection>

                <FormSection maxWidth="100%">
                  <div className="actions">
                    <div className="save">
                      <Button
                        category="success"
                        disabled={formState.invalid}
                        loading={saving}
                        text={messages.commonSaveChanges}
                        type="submit"
                      />
                    </div>
                    <div className="export">
                      <SiteSettingsThemesExport
                        value={JSON.stringify(formState.values.data)}
                      />
                    </div>
                  </div>
                </FormSection>
              </div>
            )}
          </Form>
        ) : null}
      </Box>

      {/* <div className="components">
        <SiteSettingsThemesComponents />
      </div> */}

      <style jsx>{`
        .content {
          margin-top: 20px;
        }
        .preview {
          margin-top: 15px;
        }
        .preview-item {
          margin-top: 15px;
        }
        .preview-item-inline {
          display: inline-block;
          margin-right: 15px;
          max-width: 300px;
        }
        .actions {
          display: flex;
        }
        .save {
          flex-grow: 1;
        }
        .export {
          margin-right: 10px;
        }
        .components {
          margin-top: 100px;
        }
      `}</style>
    </div>
  );
};

export default SiteSettingsThemesEdit;
